import { logger } from './logger';
import api from './api';

/**
 * Getty Vocabularies API - Pre-built Hierarchy Data from S3
 * Uses managed API client for automatic JWT token handling, proactive token refresh,
 * and consistent error handling.
 *
 * The backend now returns pre-built tree structures, eliminating the need for
 * frontend tree-building logic.
 *
 * CACHING: This service implements in-memory caching to prevent excessive API calls.
 * Each hierarchy is fetched once and cached for the session. Concurrent requests
 * for the same hierarchy will share a single in-flight request.
 */

/** Tree node structure for hierarchical display */
export interface TreeNode {
  id: string;
  label: string;
  children?: TreeNode[];
}

/** Response from Getty hierarchy endpoints */
export interface GettyHierarchyResponse {
  /** Pre-built tree structure ready for rendering */
  tree: TreeNode[];
  /** Flat sorted list of all labels for autocomplete */
  flatLabels: string[];
  /** Number of SPARQL results used to build the tree */
  count: number;
  /** Number of nodes in the tree */
  treeNodeCount: number;
  /** ISO timestamp when data was loaded */
  createdAt: string;
  /** Getty AAT concept ID */
  conceptId: string;
  /** Human-readable concept name */
  conceptName: string;
}

/** Cache entry type */
type CacheEntry = GettyHierarchyResponse | null;

/** In-flight request promise type */
type InFlightRequest = Promise<CacheEntry>;

class GettyApiService {
  // In-memory cache for hierarchy responses
  private cache: Map<string, CacheEntry> = new Map();

  // Track in-flight requests to prevent duplicate concurrent calls
  private inFlightRequests: Map<string, InFlightRequest> = new Map();

  /**
   * Generic method to fetch and cache hierarchy data.
   * Handles caching and prevents duplicate concurrent requests.
   */
  private async fetchHierarchyWithCache(
    cacheKey: string,
    endpoint: string,
    hierarchyName: string
  ): Promise<CacheEntry> {
    // Return cached data if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    // If there's already an in-flight request for this data, wait for it
    if (this.inFlightRequests.has(cacheKey)) {
      return this.inFlightRequests.get(cacheKey)!;
    }

    // Create the fetch promise
    const fetchPromise = (async (): Promise<CacheEntry> => {
      try {
        const response = await api.get(endpoint);
        const data = response.data as GettyHierarchyResponse;

        // Cache the result
        this.cache.set(cacheKey, data);
        return data;
      } catch (error) {
        logger.error(`Failed to fetch Getty ${hierarchyName} hierarchy`, error);
        // Cache null to prevent repeated failing requests
        this.cache.set(cacheKey, null);
        return null;
      } finally {
        // Remove from in-flight requests
        this.inFlightRequests.delete(cacheKey);
      }
    })();

    // Track the in-flight request
    this.inFlightRequests.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  async getWorkTypesHierarchy(): Promise<GettyHierarchyResponse | null> {
    return this.fetchHierarchyWithCache(
      'workTypes',
      '/api/getty/worktypes/hierarchy',
      'work types'
    );
  }

  async getMaterialsHierarchy(): Promise<GettyHierarchyResponse | null> {
    return this.fetchHierarchyWithCache(
      'materials',
      '/api/getty/materials-substrates/hierarchy',
      'materials-substrates'
    );
  }

  async getTechniquesHierarchy(): Promise<GettyHierarchyResponse | null> {
    return this.fetchHierarchyWithCache(
      'techniques',
      '/api/getty/techniques/hierarchy',
      'techniques'
    );
  }

  /**
   * Clear the cache. Useful for testing or forcing a refresh.
   */
  clearCache(): void {
    this.cache.clear();
  }

  // Alias methods that return string arrays for autocomplete
  // Now uses pre-built flatLabels instead of extracting from SPARQL bindings
  async getWorkTypes(): Promise<string[]> {
    const hierarchy = await this.getWorkTypesHierarchy();
    return hierarchy?.flatLabels ?? [];
  }

  async getTechniques(): Promise<string[]> {
    const hierarchy = await this.getTechniquesHierarchy();
    return hierarchy?.flatLabels ?? [];
  }

  async getMaterials(): Promise<string[]> {
    const hierarchy = await this.getMaterialsHierarchy();
    return hierarchy?.flatLabels ?? [];
  }

  // Substrates use the same materials-substrates hierarchy
  async getSubstrates(): Promise<string[]> {
    const hierarchy = await this.getMaterialsHierarchy();
    return hierarchy?.flatLabels ?? [];
  }
}

export const gettyApi = new GettyApiService();
